/**
 * Attachments as the session history returns them, and as the transcript wants
 * them.
 *
 * Split out of LazyPrompt because the mapping has a rule in it worth testing on
 * its own: a turn row carries both what the user attached and what the agent
 * generated, and they belong on different messages.
 */

/** One attachment as `GET /sessions/{id}` returns it. */
export interface HistoryAttachment {
	id: number;
	/** chat = the user attached it; generated = a tool made it; screenshot = a page the agent looked at. */
	kind: 'chat' | 'generated' | 'screenshot';
	type: 'image' | 'file';
	name: string;
	mime_type?: string;
	url: string;
	size_bytes?: number;
	expired?: boolean;
}

export interface Attachment {
	id: string;
	type: 'image' | 'file';
	name: string;
	/**
	 * What to render.
	 *
	 * For an attachment the user has just picked this is a `blob:` object URL
	 * over the local `File`, valid only for this document — a refresh and it is
	 * dead. For one replayed out of history it is the server path the file was
	 * stored at, which survives everything and is fetched on demand.
	 * `AttachmentThumb` is what tells the two apart.
	 */
	url: string;
	mimeType: string;
	file?: File;
	/**
	 * Set on attachments replayed from history. True once the file has passed
	 * the lifetime it was stored with and the retention job is entitled to have
	 * deleted it — a claim, not a guarantee, so the render also treats a failed
	 * fetch as the same state. Never set for a generated image: those are
	 * stored with no lifetime and do not expire.
	 */
	expired?: boolean;
	sizeBytes?: number;
}

function toAttachments(raw: HistoryAttachment[], prefix: string): Attachment[] {
	return raw.map((a, idx) => ({
		id: `${prefix}_${a.id ?? idx}`,
		type: a.type === 'file' ? 'file' : 'image',
		name: a.name,
		url: a.url,
		mimeType: a.mime_type ?? '',
		expired: a.expired,
		sizeBytes: a.size_bytes,
	}));
}

/**
 * Split one turn's attachments between the two messages it becomes.
 *
 * What the user attached goes back onto their own message; what a tool
 * generated, and the screenshots the agent took, go onto the reply. Both arrive on the same history row because
 * both belong to the same turn, and putting a generated image above the
 * question that produced it would read as the user having supplied it.
 */
export function splitTurnAttachments(
	raw: HistoryAttachment[] | undefined,
	index: number,
): { user: Attachment[]; assistant: Attachment[] } {
	if (!raw?.length) return { user: [], assistant: [] };
	return {
		user: toAttachments(
			raw.filter(a => a.kind === 'chat' || !a.kind),
			`hist_att_u_${index}`,
		),
		assistant: toAttachments(
			raw.filter(a => a.kind === 'generated' || a.kind === 'screenshot'),
			`hist_att_a_${index}`,
		),
	};
}

/** A file as the chat endpoint takes it: raw base64, without the `data:...;base64,` prefix. */
export function fileToBase64(file: File): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => {
			const result = reader.result as string;
			resolve(result.split(',')[1] ?? result);
		};
		reader.onerror = reject;
		reader.readAsDataURL(file);
	});
}

/** The chat's `attachments` payload for what the user picked. */
export async function toChatAttachments(attachments: Attachment[]): Promise<any[]> {
	return Promise.all(
		attachments.map(async a => {
			const att: any = { type: a.type, name: a.name, mime_type: a.mimeType };
			if (a.file) att.data = await fileToBase64(a.file);
			// An existing file picked from the files service travels as its path: the agent is
			// told where it is and fetches it only if it needs the bytes.
			else if (a.url && !a.url.startsWith('blob:')) att.url = a.url;
			return att;
		}),
	);
}

const IMAGE_EXT = /\.(png|jpe?g|gif|webp|svg|avif|bmp|ico)$/i;
const MIME_BY_EXT: Record<string, string> = {
	pdf: 'application/pdf',
	txt: 'text/plain',
	csv: 'text/csv',
	json: 'application/json',
	xml: 'application/xml',
	svg: 'image/svg+xml',
	png: 'image/png',
	jpg: 'image/jpeg',
	jpeg: 'image/jpeg',
	gif: 'image/gif',
	webp: 'image/webp',
};

/**
 * An attachment for a file picked from the static or secured store: a server path, no `File`.
 * The FileBrowser answers with `api/files/<store>/file/<client>/<path>`, with or without a
 * leading slash.
 */
export function pickedFileAttachment(url: string, id: string): Attachment {
	const path = url.startsWith('/') || /^https?:/.test(url) ? url : `/${url}`;
	const name = decodeURIComponent(path.split('?')[0].split('/').pop() || 'file').replace(
		/\+/g,
		' ',
	);
	const ext = (name.split('.').pop() || '').toLowerCase();
	return {
		id,
		type: IMAGE_EXT.test(name) ? 'image' : 'file',
		name,
		url: path,
		mimeType: MIME_BY_EXT[ext] ?? '',
	};
}
